import {
  Button,
  Card,
  CardContent,
  Chip,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Add,
  Description,
  Download,
  MoreVert,
  PictureAsPdf,
  Settings,
  Upload,
  FolderOpen,
  SendOutlined,
} from "@mui/icons-material";
import Grid from "@mui/material/Grid";
import { formatDate } from "../../../utils/formatters";
import React, { useState } from "react";
import { InformationSnippet } from "../../../shared-components/InformationSnippet";
import { AssignmentDeleteMenuItem } from "./AssignmentDeleteMenuItem";
import { useNavigate } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useApiClient } from "../../../api";
import { enqueueSnackbar } from "notistack";
import { AddTagsDialog } from "../../../shared-components/ExpandableTagsBox";
import Menu from "@mui/material/Menu";
import { CreateWithBudButton } from "../../assistant/components/CreateWithBudButton";
import LinkedQuizGroups from "../../quiz/components/LinkedQuizGroupsAccordion";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import { checkIfIsIndependentUser } from "../../auth/Utils";
import { AssignmentPreferencesDialog } from "./AssignmentPreferencesDialog";
import { ReleaseFeedbackDialog } from "./ReleaseFeedbackDialog";
import TieredFeatureAccess from "../../../shared-components/TieredFeatureAccess";
dayjs.extend(customParseFormat);

export const AssignmentSummary = ({
  assignment,
  setAssignment,
  handleExportClicked,
  handleExportAllFeedback,
  handleDownloadCoverSheets,
  handleExportResults,
  handleUploadClicked,
  assignmentQuestion = null,
  onRefresh = () => {},
}) => {
  const navigate = useNavigate();
  const ApiClient = useApiClient();
  const [addTagsOpen, setAddTagsOpen] = useState(false);
  const [newTags, setNewTags] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [preferencesDialogOpen, setPreferencesDialogOpen] = useState(null);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [releaseFeedbackDialogOpen, setReleaseFeedbackDialogOpen] =
    useState(false);

  const isIndependentUser = checkIfIsIndependentUser();
  const open = Boolean(anchorEl);

  const handleAssignmentGroupClick = () => {
    if (assignment.assignmentGroup) {
      navigate(`/assignments/groups/${assignment.assignmentGroup.id}`);
    } else {
      setGroupName(assignment.name);
      setCreateGroupDialogOpen(true);
    }
  };

  const handleCreateGroup = async () => {
    try {
      setCreatingGroup(true);
      const result = await ApiClient.assignment.createAssignmentGroup({
        name: groupName,
        assignment: assignment.id,
      });

      if (result?.success) {
        enqueueSnackbar(result.message, {
          variant: "success",
          autoHideDuration: 3000,
        });
        const groupId = result.assignmentGroup.id;
        setCreateGroupDialogOpen(false);
        navigate(`/assignments/groups/${groupId}`);
      }
    } catch (error) {
      console.error("Failed to create assignment group", error);
      enqueueSnackbar("Failed to create assignment group", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const updateAssignment = async ({ dueDate, tags }) => {
    try {
      setUpdating(true);
      const res = await ApiClient.assignment.updateAssignment({
        assignment: assignment.id,
        dueDate: dayjs(dueDate).format("YYYY-MM-DD"),
        tags: tags.map((t) => t.tag),
      });
      if (res?.success) {
        setAssignment({ ...assignment, dueDate: dueDate, tags: tags });
        enqueueSnackbar("Successfully updated assignment", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setAddTagsOpen(false);
      } else {
        enqueueSnackbar("Failed to update assignment", {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (err) {
      console.error("Failed to update assignment", err);
      enqueueSnackbar("Failed to update assignment", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleDueDateChange = (newDueDate) => {
    updateAssignment({
      dueDate: newDueDate,
      tags: assignment.tags,
    });
  };

  const handleAddTags = () => {
    const newTagObjects = newTags.map((tagStr) => ({
      id: `temp-${Math.floor(Math.random() * 10000)}`,
      tag: tagStr,
    }));

    const updatedTags = [...assignment.tags, ...newTagObjects];

    updateAssignment({
      dueDate: assignment.dueDate,
      tags: updatedTags,
    });
  };

  const handleReUseClicked = () => {
    navigate(
      `/assignments/view/template/${assignment.assignmentTemplate.id}?reuse=True`,
    );
  };

  const DueDatePicker = ({ value, onChange }) => {
    const parsedValue =
      value && typeof value === "string"
        ? dayjs(value, ["DD/MM/YYYY", "YYYY-MM-DD", dayjs.ISO_8601], true)
        : dayjs(value);

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          format="DD/MM/YYYY"
          value={parsedValue.isValid() ? parsedValue : null}
          minDate={dayjs()}
          onChange={(newValue) =>
            onChange(newValue ? newValue.toISOString() : null)
          }
        />
      </LocalizationProvider>
    );
  };

  const preferences = JSON.parse(assignment?.preferences?.data);

  return (
    <>
      <ReleaseFeedbackDialog
        open={releaseFeedbackDialogOpen}
        setOpen={setReleaseFeedbackDialogOpen}
        assignment={assignment}
        onSuccess={onRefresh}
      />
      <Dialog
        open={createGroupDialogOpen}
        onClose={() => setCreateGroupDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Assignment Group</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateGroupDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateGroup}
            variant="contained"
            disabled={!groupName.trim() || creatingGroup}
          >
            Create Group
          </Button>
        </DialogActions>
      </Dialog>
      <AssignmentPreferencesDialog
        assignment={assignment}
        open={preferencesDialogOpen}
        setOpen={setPreferencesDialogOpen}
      />
      <AddTagsDialog
        open={addTagsOpen}
        onClose={() => setAddTagsOpen(false)}
        tags={assignment.tags}
        subject={assignment.schoolClass.subject}
        educationLevel={assignment.schoolClass.yearGroup.educationLevel}
        newTags={newTags}
        setNewTags={setNewTags}
        onSave={handleAddTags}
        saving={updating}
      />
      <Card>
        <CardContent>
          <Stack
            direction={"row"}
            alignItems={"center"}
            justifyContent={"space-between"}
          >
            <Typography variant="h3">{assignment.name}</Typography>
            <div>
              {assignment.feedbackManuallyReleased ? (
                <Chip label={"Feedback released"} sx={{ mr: 1 }} />
              ) : (
                <Tooltip
                  title={
                    preferences.auto_release_marks
                      ? "Your preferences automatically release feedback for this assignment"
                      : ""
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={() => setReleaseFeedbackDialogOpen(true)}
                      endIcon={<SendOutlined />}
                      disabled={Boolean(preferences.auto_release_marks)}
                      sx={{ mr: 1 }}
                    >
                      Release Feedback
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Button
                variant="contained"
                onClick={handleMenuClick}
                endIcon={<MoreVert />}
              >
                Actions
              </Button>
              {!isIndependentUser && (
                <IconButton
                  onClick={() => setPreferencesDialogOpen(true)}
                  sx={{ mr: 1, border: 0 }}
                  disabled={!assignment?.preferences}
                >
                  <Settings />
                </IconButton>
              )}
              <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
                {handleExportClicked && (
                  <MenuItem
                    onClick={() => {
                      handleExportClicked();
                      handleMenuClose();
                    }}
                  >
                    <PictureAsPdf fontSize="small" sx={{ mr: 1 }} />
                    Download Assignment
                  </MenuItem>
                )}
                {handleUploadClicked && (
                  <TieredFeatureAccess
                    menuItem
                    obj={assignment}
                    feature="bulk_upload"
                  >
                    <MenuItem
                      onClick={() => {
                        handleUploadClicked();
                        handleMenuClose();
                      }}
                    >
                      <Upload fontSize="small" sx={{ mr: 1 }} />
                      Upload Answers
                    </MenuItem>
                  </TieredFeatureAccess>
                )}
                <MenuItem
                  onClick={() => {
                    handleExportAllFeedback();
                    handleMenuClose();
                  }}
                  disabled={assignment.answersCount === 0}
                >
                  <Download fontSize="small" sx={{ mr: 1 }} />
                  Download Feedback
                </MenuItem>
                {handleDownloadCoverSheets &&
                  assignment.schoolClass.official && (
                    <TieredFeatureAccess
                      menuItem
                      obj={assignment}
                      feature="bulk_upload"
                    >
                      <MenuItem
                        onClick={() => {
                          handleDownloadCoverSheets();
                          handleMenuClose();
                        }}
                      >
                        <Description fontSize="small" sx={{ mr: 1 }} />
                        Download Cover Sheets
                      </MenuItem>
                    </TieredFeatureAccess>
                  )}

                {handleExportResults && (
                  <Tooltip
                    title={
                      isIndependentUser &&
                      "Only available on school subscriptions"
                    }
                  >
                    <span>
                      <TieredFeatureAccess
                        menuItem
                        obj={assignment}
                        feature="export_results"
                      >
                        <MenuItem
                          onClick={() => {
                            handleExportResults();
                            handleMenuClose();
                          }}
                          disabled={isIndependentUser}
                        >
                          <FruitifyIcon
                            name={"CSVFile"}
                            fontSize="small"
                            sx={{ mr: 1 }}
                          />
                          Export results
                        </MenuItem>
                      </TieredFeatureAccess>
                    </span>
                  </Tooltip>
                )}
                <MenuItem
                  onClick={() => {
                    handleAssignmentGroupClick();
                    handleMenuClose();
                  }}
                  disabled={creatingGroup}
                >
                  <FolderOpen fontSize="small" sx={{ mr: 1 }} />
                  {assignment.assignmentGroup
                    ? "View Assignment Group"
                    : "Create Assignment Group"}
                </MenuItem>
                <MenuItem>
                  <CreateWithBudButton
                    kind="quiz"
                    assignment={assignment}
                    assignmentQuestion={assignmentQuestion}
                  >
                    Ask Bud to create a quiz
                  </CreateWithBudButton>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleReUseClicked();
                    handleMenuClose();
                  }}
                >
                  <Add fontSize="small" sx={{ mr: 1 }} />
                  Re-use
                </MenuItem>
                <AssignmentDeleteMenuItem
                  assignmentId={assignment.id}
                  disabled={assignment.answersCount > 0}
                />
              </Menu>
            </div>
          </Stack>
          <Grid container spacing={2} direction={"row"} sx={{ mt: 2, mb: 2 }}>
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <InformationSnippet
                title={"Class"}
                value={assignment.schoolClass.name}
              />
            </Grid>
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <InformationSnippet
                title={"Subject"}
                value={assignment.schoolClass.subject.name}
              />
            </Grid>
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <InformationSnippet
                title={"Due Date"}
                value={formatDate(assignment.dueDate)}
                EditableComponent={DueDatePicker}
                onSave={handleDueDateChange}
              />
            </Grid>
            <Grid size={{ md: 3, sm: 6, xs: 12 }}>
              <InformationSnippet
                title={"Tags"}
                useTagBox
                value={Array.isArray(assignment.tags) ? assignment.tags : []}
                allowAddTags
                onAddTagsClicked={() => setAddTagsOpen(true)}
              />
            </Grid>
          </Grid>
          <LinkedQuizGroups assignment={assignment} onRefresh={onRefresh} />
        </CardContent>
      </Card>
    </>
  );
};
